import { IconButton } from '@zendeskgarden/react-buttons'
import { Tooltip } from '@zendeskgarden/react-tooltips'
import debounce from 'lodash.debounce'
import React, { MouseEvent, useEffect, useRef } from 'react'
import Moment from 'react-moment'
import { Link } from 'react-router-dom'
import styled from 'styled-components'

import PlusIcon from '../../icons/plus.svg?react'
import { BaseRecord } from '../../models/BaseRecord'
import { Message } from '../../models/Message'
import { Team } from '../../models/Team'
import { User } from '../../models/User'
import { getTeamName } from '../../utils/teams'
import { Spinner } from '../Spinner'
import { ContactAvatar } from '../contacts/ContactAvatarV1'
import { GroupAvatar } from '../contacts/GroupAvatar'
import { SearchField } from '../common/fields/SearchField'
import { InboxState } from './MessagesPage'

const InboxList = styled.div`
    overscroll-behavior: contain;
    overflow-y: auto;
`

const InboxEntry = styled(Link)`
    text-decoration: none;
    margin-left: 8px;
    margin-right: 8px;
    color: rgb(47, 57, 65);
    display: flex;
    padding: 16px 21px 16px 16px;
    &:focus,
    &:visited,
    &:link,
    &:active,
    &:hover {
        color: rgb(47, 57, 65);
        text-decoration: none;
    }
`

const InboxRow = styled.div`
    display: flex;
    width: 100%;
    overflow-x: hidden;
    flex-direction: column;
`

const AvatarWrapper = styled.div`
    width: 48px;
    margin-right: 16px;
`

interface InboxViewProps {
    inboxState: InboxState
    currentThreadID: string | undefined
    inboxHeight: number
    onSelectThread: (thread: BaseRecord) => void
    onNewMessage: () => void
    onSearch: (query: string) => void
    onClearSearch: () => void
    onLoadMore: () => void
}

export const InboxView: React.FC<InboxViewProps> = ({
    inboxState,
    currentThreadID,
    inboxHeight,
    onSelectThread,
    onNewMessage,
    onSearch,
    onClearSearch,
    onLoadMore,
}) => {
    const { isSending, inbox, isSearching, didSearch, searchResults } = inboxState

    const inboxListRef = useRef<HTMLDivElement>(null)

    const handleScroll = debounce(() => {
        if (!inboxListRef.current) return
        const { scrollTop, scrollHeight, offsetHeight } = inboxListRef.current
        const percentToBottom = scrollTop / (scrollHeight - offsetHeight)
        if (percentToBottom > 0.75 && !isSending) {
            console.log('Load more')
            onLoadMore()
        }
    }, 200)

    // Automatically load more inbox messages when the scroll bar is near the bottom
    useEffect(() => {
        const inboxList = inboxListRef.current
        if (inboxList) {
            inboxList.addEventListener('scroll', handleScroll)
        }
        return () => {
            if (inboxList) {
                inboxList.removeEventListener('scroll', handleScroll)
            }
        }
    }, [handleScroll])

    const onClickThread = (e: MouseEvent, message: Message) => {
        e.preventDefault()
        const threadId = message.person?.id || message.team?.id
        if (threadId) {
            onSelectThread(message.person || message.team!)
        }
    }

    const calendarStrings = {
        sameDay: 'LT',
        lastDay: '[Yesterday]',
        lastWeek: 'dddd',
        sameElse: 'MMM D',
    }

    return (
        <div
            style={{
                minWidth: '440px',
                borderRight: '1px solid #e4e4e4',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <div style={{ display: 'flex' }}>
                <div
                    style={{
                        padding: '21px 0 23px 26px',
                        fontSize: '14px',
                        lineHeight: '24px',
                        color: '#828282',
                        fontWeight: 700,
                    }}
                >
                    Messages
                </div>
                <Tooltip content="Create new message" placement="top">
                    <IconButton
                        onClick={onNewMessage}
                        style={{ margin: '21px 40px 0 auto', width: '32px', borderRadius: '16px' }}
                    >
                        <PlusIcon />
                    </IconButton>
                </Tooltip>
            </div>
            <div
                style={{
                    height: '1px',
                    backgroundColor: '#e4e4e4',
                    width: '100%',
                    marginBottom: '12px',
                }}
            />
            {!isSending && (
                <div className="my-4 flex justify-center">
                    <SearchField
                        className="w-[378px]"
                        size="medium"
                        placeholder="Search messages by name"
                        value={inboxState.searchText}
                        onSearch={onSearch}
                        onClear={onClearSearch}
                    />
                </div>
            )}
            {(isSending || isSearching) && (
                <div
                    style={{
                        position: 'absolute',
                        marginLeft: '-64px',
                        width: '440px',
                        transform: 'translate(50%, 50%)',
                        top: 'calc(50% - 64px)',
                    }}
                >
                    <Spinner />
                </div>
            )}
            <InboxList
                id="inboxList"
                ref={inboxListRef}
                style={{ height: inboxHeight - 128, scrollbarWidth: 'none' }}
            >
                {didSearch && !isSearching && searchResults.length === 0 && (
                    <div style={{ textAlign: 'center' }}>No matching contacts found</div>
                )}
                {didSearch &&
                    !isSearching &&
                    searchResults.map((message) => {
                        const threadId = message.person?.id
                        const contact = message.person?.id ? message.person : null
                        if (!contact) return null

                        return (
                            <InboxEntry
                                key={`inbox-${threadId}`}
                                to={`/messages/${threadId}`}
                                onClick={(e) => onClickThread(e, message)}
                            >
                                <AvatarWrapper>
                                    <ContactAvatar user={contact} />
                                </AvatarWrapper>
                                <InboxRow>
                                    <div
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'row',
                                            lineHeight: '24px',
                                        }}
                                    >
                                        <div
                                            style={{
                                                fontSize: '14px',
                                                color: '#000000',
                                                fontWeight: 400,
                                            }}
                                        >
                                            {contact.name}
                                        </div>
                                    </div>
                                    <span style={{ fontSize: '13px' }}>
                                        <div
                                            style={{
                                                color: '#999999',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'pre',
                                            }}
                                        >
                                            Click to open message thread
                                        </div>
                                    </span>
                                </InboxRow>
                            </InboxEntry>
                        )
                    })}
                {!didSearch &&
                    !isSearching &&
                    inbox.map((message) => {
                        const threadId = message.person?.id || message.team?.id
                        const contact = message.person?.id
                            ? message.person
                            : message.team?.id
                              ? message.team
                              : null
                        if (!contact) return null

                        const threadName = message.person?.id
                            ? contact.name
                            : getTeamName(contact as Team)
                        const threadAvatar = message.person?.id ? (
                            <AvatarWrapper>
                                <ContactAvatar user={contact as User} />
                            </AvatarWrapper>
                        ) : (
                            <AvatarWrapper>
                                <GroupAvatar />
                            </AvatarWrapper>
                        )

                        const selected = threadId === currentThreadID

                        return (
                            <InboxEntry
                                key={`inbox-${threadId}`}
                                className={selected ? 'active' : ''}
                                to={`/messages/${threadId}`}
                                onClick={(e) => onClickThread(e, message)}
                            >
                                {!selected && !message.viewedDate && (
                                    <div
                                        style={{
                                            margin: 'auto 0 auto -16px',
                                            width: '20px',
                                            color: '#E22216',
                                        }}
                                    >
                                        ●
                                    </div>
                                )}
                                {threadAvatar}
                                <InboxRow>
                                    <div
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'row',
                                            lineHeight: '24px',
                                        }}
                                    >
                                        <div
                                            style={{
                                                fontSize: '14px',
                                                color: selected ? '' : '#000000',
                                                fontWeight: selected ? 600 : 400,
                                            }}
                                        >
                                            {threadName}
                                        </div>
                                        <Moment
                                            style={{
                                                fontSize: '11px',
                                                marginLeft: 'auto',
                                                color: selected ? '' : '#999999',
                                            }}
                                            calendar={calendarStrings}
                                        >
                                            {message.createdDate}
                                        </Moment>
                                    </div>
                                    <span style={{ fontSize: '13px' }}>
                                        <div
                                            style={{
                                                color: selected ? '' : '#999999',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'pre',
                                            }}
                                        >
                                            {message.message && message.message.replace(/\n/g, ' ')}
                                        </div>
                                    </span>
                                </InboxRow>
                            </InboxEntry>
                        )
                    })}
            </InboxList>
        </div>
    )
}
